import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaVideo, FaClock } from 'react-icons/fa';
import JitsiRoom from '../../Components/VideoConsultation/JitsiRoom';
import { fetchGuestSession, markGuestJoined } from '../../Services/videoConsultation';

const formatWhen = (session) => {
  if (!session?.bookDate) return '';
  const slot = session.bookTimeSlot;
  const time = slot?.startTime ? ` at ${slot.startTime}` : '';
  return `${session.bookDate}${time}`;
};

const Shell = ({ children }) => (
  <div className="flex min-h-[80vh] items-center justify-center bg-slate-100 p-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      {children}
    </div>
  </div>
);

export default function PatientVideoConsultation() {
  const { token } = useParams();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inRoom, setInRoom] = useState(false);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await fetchGuestSession(token);
      setSession(data);
      setName((prev) => prev || data.patientName || '');
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'This consultation link is not valid');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // While the patient is waiting for the room to open, re-check periodically so
  // the Join button appears without them having to refresh.
  useEffect(() => {
    if (inRoom || error || session?.canJoin !== false) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [inRoom, error, session?.canJoin, load]);

  const join = async () => {
    try {
      await markGuestJoined(token);
      setInRoom(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not join the consultation');
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" />
        <p className="mt-4 text-sm text-slate-500">Loading your consultation…</p>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <p className="font-medium text-rose-600">{error}</p>
        <p className="mt-2 text-sm text-slate-500">
          Please contact the clinic if you believe this is a mistake.
        </p>
      </Shell>
    );
  }

  if (inRoom) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-900">
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          <p className="truncate text-sm font-medium text-slate-200">
            Consultation with {session.doctorName}
          </p>
          <span className="shrink-0 text-xs text-slate-400">{formatWhen(session)}</span>
        </div>
        <JitsiRoom
          domain={session.domain}
          roomName={session.roomName}
          displayName={name || session.patientName}
          subject={`Consultation with ${session.doctorName}`}
          onLeft={() => setInRoom(false)}
          className="min-h-0 flex-1"
        />
      </div>
    );
  }

  const opensAt = session.opensAt ? new Date(session.opensAt) : null;

  return (
    <Shell>
      <FaVideo className="mx-auto h-10 w-10 text-violet-500" />
      <h1 className="mt-4 text-lg font-bold text-slate-800">Your online consultation</h1>
      <p className="mt-1 text-sm text-slate-500">
        with {session.doctorName} · {formatWhen(session)}
      </p>

      {session.canJoin ? (
        <>
          <div className="mt-6 text-left">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Your name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
          </div>
          <button
            type="button"
            onClick={join}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 font-medium text-white transition hover:bg-sky-700"
          >
            <FaVideo /> Join consultation
          </button>
          <p className="mt-3 text-xs text-slate-500">
            Your browser will ask for camera and microphone access.
          </p>
        </>
      ) : (
        <div className="mt-6 rounded-xl bg-amber-50 p-4 text-amber-800">
          <FaClock className="mx-auto h-5 w-5" />
          <p className="mt-2 text-sm font-medium">The room is not open yet</p>
          <p className="mt-1 text-xs">
            {opensAt
              ? `You can join from ${opensAt.toLocaleString()}.`
              : 'Please come back at your appointment time.'}
          </p>
          <p className="mt-2 text-xs text-amber-700/80">This page checks again automatically.</p>
        </div>
      )}
    </Shell>
  );
}
